import { Location } from '@angular/common';
import { Component, HostListener, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { OptionWindowModel } from 'src/app/models/authentication/option-window.model';
import { CompositeArticleModel } from 'src/app/models/preliminaries/composite-article.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { CompositeArticleService } from 'src/app/services/preliminaries/composite-article.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-composite-article',
  templateUrl: './register-composite-article.component.html',
  styleUrl: './register-composite-article.component.scss',
  standalone: false
})
export class RegisterCompositeArticleComponent {

  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly compositeArticleService = inject(CompositeArticleService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly headerService = inject(HeaderService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly articleService = inject(ArticleService);
  private readonly title = inject(Title);
  private readonly location = inject(Location);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly windowService = inject(WindowService);

  companyId: number = 0;
  formCompositeArticle: FormGroup = new FormGroup({});
  iconState: string = '';
  isOpened: string | undefined = undefined;
  compositeArticleContract: CompositeArticleModel = new CompositeArticleModel();
  compositeArticleId: number = 0;
  nameWindow: string = 'Captura Artículos Compuestos';
  permissionPrint: boolean = false;
  permissionUpdatable: boolean = false;
  permissionsUser: OptionWindowModel[] = [];
  projectName = ProjectName.projectName;
  quantityDetails: number = 0;
  severityState: string = '';
  stateCompositeArticle: string = '';
  titleState: string = '';
  url: string = 'composite-article';
  listFamily: any[] = [];
  listGroup: any[] = [];
  listSubgroup: any[] = [];

  async ngOnInit() {
    this.validateForm(new CompositeArticleModel());
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.activatedRoute.params.subscribe(params => {
      this.compositeArticleId = Object.keys(params).length > 0 ? params?.id : null;
    });
    await this.handleInitialState();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async changeCode(code: number) {
    if (!code) {
      this.resetContract();
      return;
    }
    try {
      const response = await firstValueFrom(
        this.compositeArticleService.getCompositeArticleByKeys(code)
      );
      this.compositeArticleId = response.articleId;
      this.compositeArticleContract = response;
      this.validateForm(this.compositeArticleContract);
      this.location.replaceState(`/${this.url}/register/${this.compositeArticleId}`);
    } catch (error) {
      console.error('Error al buscar el artículo compuesto.', error);
    }
  }

  async changeState() {
    if (!this.compositeArticleId) {
      this.toastHandlerService.showToast(
        'warning',
        'Debe crear un artículo compuesto.'
      );
      return;
    }
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        `No tiene permisos para cambiar el estado del artículo compuesto. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    try {
      const response = await firstValueFrom(
        this.compositeArticleService.changeState(this.compositeArticleContract.articleId)
      );
      this.compositeArticleContract = response;
      this.validateForm(this.compositeArticleContract);
      if (this.compositeArticleContract.active) {
        this.toastHandlerService.showToast(
          'success',
          'El artículo compuesto fue activado exitosamente.'
        );
      } else {
        this.toastHandlerService.showToast(
          'success',
          'El artículo compuesto fue inactivado exitosamente.'
        );
      }
    } catch (error) {
      console.error('Error al cambiar el estado del artículo compuesto.', error);
    }
  }

  async createNewRegister() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para crear un artículo compuesto. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    this.resetContract();
  }

  async createCompositeArticle(modelcompositeArticle: CompositeArticleModel) {
    try {
      const response = await firstValueFrom(
        this.compositeArticleService.createCompositeArticle(modelcompositeArticle)
      );
      this.toastHandlerService.showToast(
        'success',
        `El artículo compuesto fue creado exitosamente.`
      );
      this.compositeArticleId = response.articleId;
      this.compositeArticleContract = response;
      this.validateForm(this.compositeArticleContract);
      this.location.replaceState(`/${this.url}/register/${this.compositeArticleId}`);
    } catch (error) {
      console.error('Error al crear el artículo compuesto.', error);
    }
  }

  async deleteCompositeArticle() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        `El usuario no tiene permisos para eliminar el artículo compuesto. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    if (!this.compositeArticleId) {
      this.toastHandlerService.showToast(
        'warning',
        'Debe crear un artículo compuesto.'
      );
      return;
    }
    if (this.quantityDetails > 0) {
      this.toastHandlerService.showToast(
        'warning',
        'No se puede eliminar un artículo compuesto mientras tenga detalle.'
      );
      return;
    }
    const accepted = await this.confirmDialogService.confirm('¿Realmente desea eliminar el artículo compuesto?');
    if (accepted) {
      try {
        await firstValueFrom(
          this.compositeArticleService.deleteCompositeArticle(this.compositeArticleId)
        );
        this.toastHandlerService.showToast(
          'success',
          'El artículo compuesto fue eliminado exitosamente.'
        );
        this.resetContract();
      } catch (error) {
        console.error('Error al eliminar el artículo compuesto.', error);
      }
    }
  }

  async getAllLists(...listsToLoad: Array<'families' | 'groups' | 'subgroups'>) {
    try {
      const lists = {
        families: async () => {
          this.listFamily = await firstValueFrom(
            this.articleService.getAllFamiliesForSelectAsync()
          );
        },
        groups: async () => {
          this.listGroup = await firstValueFrom(
            this.articleService.getAllGroupsForSelectAsync()
          );
        },
        subgroups: async () => {
          this.listSubgroup = await firstValueFrom(
            this.articleService.getAllSubgroupsForSelectAsync()
          );
        }
      };
      if (listsToLoad.length) {
        await Promise.all(listsToLoad.map(list => lists[list]()));
      } else {
        await Promise.all([
          lists.families(),
          lists.groups(),
          lists.subgroups()
        ]);
      }
    } catch (error) {
      console.error('Error al obtener las listas.', error);
    }
  }

  async getButtonClick(buttonClick: any) {
    await this.handleCompositeArticleNavigation(buttonClick)
  }

  async getCompositeArticleById() {
    try {
      const response = await firstValueFrom(
        this.compositeArticleService.getCompositeArticleById(this.compositeArticleId)
      );
      this.compositeArticleContract = response;
      this.validateForm(this.compositeArticleContract);
    } catch (error) {
      console.error('Error al obtener el artículo compuesto.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await Promise.all([
      this.getAllLists(),
      this.getPermissionProfileByUrl()
    ]);
  }

  async getPermissionProfileByUrl() {
    try {
      const response = await firstValueFrom(
        this.windowService.getPermissionProfileByUrl(this.url)
      );
      this.permissionUpdatable = response;
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }


  async getSubgroup(subgroupId: number | null) {
    const subgroup = subgroupId ? this.listSubgroup.find(x => x.value === subgroupId) : null;

    setTimeout(() => {
      this.formCompositeArticle.patchValue({
        groupId: subgroup?.groupId ?? 0,
        subgroupId: subgroup ? subgroupId : 0
      });
    })
  }

  async handleInitialState() {
    await this.getPermission();
    if (this.compositeArticleId) {
      await this.getCompositeArticleById();
    } else {
      this.createNewRegister();
    }
  }

  async handleCompositeArticleNavigation(buttonClick: any) {
    const code = this.formCompositeArticle.get("code")?.value;
    const actionMap: Record<string, () => Promise<CompositeArticleModel>> = {
      first: () =>
        firstValueFrom(
          this.compositeArticleService.getFirstCompositeArticleByKeys()
        ),
      previus: () =>
        firstValueFrom(
          this.compositeArticleService.getPreviusCompositeArticleByKeys(code)
        ),
      next: () =>
        firstValueFrom(
          this.compositeArticleService.getNextCompositeArticleByKeys(code)
        ),
      last: () =>
        firstValueFrom(
          this.compositeArticleService.getLastCompositeArticleByKeys()
        ),
    };
    const action = actionMap[buttonClick.target.id];
    if (!action) {
      this.resetContract();
      return;
    }
    try {
      const compositeArticle = await action();
      this.compositeArticleId = compositeArticle.articleId;
      this.compositeArticleContract = compositeArticle;
      this.validateForm(this.compositeArticleContract);
      this.location.replaceState(`/${this.url}/register/${this.compositeArticleId}`);
    } catch (error) {
      console.error(`Error al buscar el artículo compuesto (${buttonClick.target.id}).`, error);
    }
  }

  async resetContract() {
    this.compositeArticleId = 0;
    this.compositeArticleContract = new CompositeArticleModel();
    this.validateForm(this.compositeArticleContract);
    this.location.replaceState(`/${this.url}/register`);
  }

  async send() {
    this.validateFormControls();
    if (this.formCompositeArticle.invalid) {
      this.showValidationMessages();
      return;
    }
    const model = this.formCompositeArticle.getRawValue() as CompositeArticleModel;

    if (this.compositeArticleId == 0) {
      await this.createCompositeArticle(model);
    } else {
      await this.updateCompositeArticle(model);
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      alterCode: {
        maxlength: 'El Código Alternativo no puede tener más de 20 caracteres.'
      },
      alterCode2: {
        maxlength: 'El Código Alternativo 2 no puede tener más de 20 caracteres.'
      },
      name: {
        required: 'Debe ingresar el Nombre.',
        maxlength: 'El Nombre no puede tener más de 300 caracteres.'
      },
      familyId: {
        required: 'Debe seleccionar una Familia.',
        min: 'Debe seleccionar una Familia válida.'
      },
      groupId: {
        required: 'Debe seleccionar un Grupo.',
        min: 'Debe seleccionar un Grupo válido.'
      },
      subgroupId: {
        required: 'Debe seleccionar un Subgrupo.',
        min: 'Debe seleccionar un Subgrupo válido.'
      },
      active: {
        required: 'Debe seleccionar un Estado.'
      },
      observation: {
        max: 'La Observación no puede tener más de 250 caracteres.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formCompositeArticle.get(controlName);
      if (control && control.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  async updateCompositeArticle(modelCompositeArticle: CompositeArticleModel) {
    this.compositeArticleContract.alterCode = modelCompositeArticle.alterCode;
    this.compositeArticleContract.alterCode2 = modelCompositeArticle.alterCode2;
    this.compositeArticleContract.name = modelCompositeArticle.name;
    this.compositeArticleContract.familyId = modelCompositeArticle.familyId;
    this.compositeArticleContract.groupId = modelCompositeArticle.groupId;
    this.compositeArticleContract.subgroupId = modelCompositeArticle.subgroupId;
    this.compositeArticleContract.active = modelCompositeArticle.active;
    this.compositeArticleContract.observation = modelCompositeArticle.observation;
    try {
      const response = await firstValueFrom(
        this.compositeArticleService.updateCompositeArticle(this.compositeArticleContract)
      );
      this.toastHandlerService.showToast(
        'success',
        'El artículo compuesto fue modificado exitosamente.'
      );
      this.compositeArticleContract = response;
      this.validateForm(this.compositeArticleContract);
    } catch (error) {
      console.error('Error al modificar el artículo compuesto.', error);
    }
  }

  updateFormState() {
    const isActive = this.compositeArticleContract.active;
    let stateConfig;
    if (isActive) {
      stateConfig = {
        icon: 'pi pi-lock-open',
        title: 'Cerrar',
        text: 'Activo',
        severityState: 'success',
      };
    } else {
      stateConfig = {
        icon: 'pi pi-lock',
        title: 'Abrir',
        text: 'Inactivo',
        severityState: 'danger',
      };
    }
    this.iconState = stateConfig.icon;
    this.titleState = stateConfig.title;
    this.stateCompositeArticle = stateConfig.text;
    this.severityState = stateConfig.severityState;

    if (isActive) {
      if (this.compositeArticleId !== 0) {
        setTimeout(() => this.formCompositeArticle.controls['code'].enable());
      } else {
        setTimeout(() => this.formCompositeArticle.controls['code'].disable());
      }
    }

    setTimeout(() => this.formCompositeArticle.controls['groupId'].disable());
    if (!this.permissionUpdatable) {
      setTimeout(() => this.formCompositeArticle.disable());
    }
  }

  validateForm(compositeArticle: CompositeArticleModel) {
    this.formCompositeArticle = this.formBuilder.group({
      code: [compositeArticle.code],
      alterCode: [compositeArticle.alterCode, [Validators.maxLength(20)]],
      alterCode2: [compositeArticle.alterCode2, [Validators.maxLength(20)]],
      name: [compositeArticle.name, [Validators.required, Validators.maxLength(300)]],
      familyId: [compositeArticle.familyId, [Validators.required, Validators.min(1)]],
      groupId: [compositeArticle.groupId, [Validators.required, Validators.min(1)]],
      subgroupId: [compositeArticle.subgroupId, [Validators.required, Validators.min(1)]],
      active: [compositeArticle.active, [Validators.required]],
      observation: [compositeArticle.observation, [Validators.max(250)]],
    });
    this.updateFormState();
  }

  validateFormControls() {
    Object.keys(this.formCompositeArticle.controls).forEach((key) => {
      const control = this.formCompositeArticle.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
